import { NextRequest } from 'next/server'
import { orbitnest } from '@/lib/orbitnest-client'
import { decryptSessionStringSync } from './crypto/session-crypt'

/**
 * Get the OrbitNest server client (service role) — server-side only.
 */
export function getOrbitnestClient() {
  return orbitnest
}

/**
 * Extract the OrbitNest bearer token from an incoming API request.
 * Checks Authorization header first, then the orbitnest-token cookie.
 */
export function getTokenFromRequest(request: NextRequest): string | null {
  const auth = request.headers.get('authorization')
  if (auth?.startsWith('Bearer ')) return auth.slice(7)
  return request.cookies.get('orbitnest-token')?.value ?? null
}

/**
 * Decode a JWT and extract the user ID (sub claim) without network calls.
 * Safe for server-side use — does not verify signature.
 */
export function getUserIdFromToken(token: string): string | null {
  try {
    const payload = JSON.parse(Buffer.from(token.split('.')[1], 'base64url').toString('utf8'))
    return payload.sub || payload.user_id || payload.id || null
  } catch {
    return null
  }
}

/**
 * Get auth headers for client-side fetch calls.
 * Reads the session from localStorage and returns the Authorization header.
 */
export function getAuthHeaders(): HeadersInit {
  if (typeof window === 'undefined') return {}

  try {
    const raw = localStorage.getItem('orbitflow_auth_session')
    if (!raw) return {}

    let session: any = decryptSessionStringSync(raw)
    if (!session) {
      try { session = JSON.parse(raw) } catch { return {} }
    }

    const actual = session?.state?.session || session
    if (!actual?.token) return {}
    if (actual.expiresAt && Date.now() >= actual.expiresAt) {
      localStorage.removeItem('orbitflow_auth_session')
      return {}
    }

    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${actual.token}`,
    }
  } catch {
    return {}
  }
}
